// Quality.  /analytics/quality
//
// The maker/checker batch test, shadow comparison and the ad-hoc retrieval and
// classification evals. The detection eval history sits at the top of the page
// and lives in ./eval_history, because it is the panel that carries the scope's
// scam-detection-accuracy evidence and it reads its own endpoint.
//
// Every rate here is gated on its own denominator. A maker/checker batch with
// no scam in it has no recall, and a shadow comparison that never ran has no
// disagreement rate. Both would otherwise print as a confident 0.

import { useState } from "react";

import { http, qs } from "../api";
import { useSession } from "../App";
import {
  Badge,
  Banner,
  Field,
  Spinner,
  Table,
  useAsync,
  useToast,
  type BadgeTone,
  type Column,
} from "../ui";
import { StatTile } from "../charts";
import { EvalHistoryPanel } from "./eval_history";
import { NotMeasuredTile, errText, num, roundPct, stamp } from "./shared";

interface QualityEval {
  result_id: string;
  recorded_at: string;
  suite: string;
  task_type: string;
  metrics: Record<string, number>;
  metadata?: Record<string, unknown> | null;
}

interface QualityOverview extends Record<string, unknown> {
  recent_evals?: QualityEval[];
  latest_drift?: { status?: string } | null;
  latest_shadow?: { status?: string; metrics?: Record<string, number> } | null;
}

interface MakerCheckerMetrics {
  n: number;
  tp: number;
  fn: number;
  fp: number;
  tn: number;
  accuracy: number;
  recall: number;
  precision: number;
  f1: number;
}

interface MakerCheckerMiss {
  id: string;
  category: string;
  language: string;
  is_scam: boolean;
  pred: string;
  reason: string;
  text: string;
}

interface MakerCheckerResult {
  total: number;
  batch_size: number;
  overall: MakerCheckerMetrics;
  by_category: Record<string, MakerCheckerMetrics>;
  decided_by: { kg: number; rule: number };
  misclassifications: MakerCheckerMiss[];
  note: string;
}

const QUALITY_METRICS: [string, string][] = [
  ["Quality evals", "quality_eval_count"],
  ["Retrieval evals", "retrieval_eval_count"],
  ["Classification evals", "classification_eval_count"],
  ["Locked evals", "locked_eval_count"],
  ["Promoted evals", "promoted_eval_count"],
  ["Active learning", "active_learning_item_count"],
  ["Low-confidence transcripts", "low_confidence_transcript_count"],
  ["Review backlog", "review_queue_backlog"],
  ["Retrieval misses", "retrieval_miss_count"],
  ["Guardrail blocks", "guardrail_block_review_count"],
];

export function QualityView() {
  const toast = useToast();
  const { can } = useSession();
  // Quality runs are governed by analytics:write OR settings.chatbot:write, the
  // same pair the server checks.
  const canRun = can("analytics", "write") || can("settings.chatbot", "write");

  const [version, setVersion] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [maker, setMaker] = useState<MakerCheckerResult | null>(null);
  const [mcTotal, setMcTotal] = useState(200);

  const [retrieval, setRetrieval] = useState({
    suite: "retrieval-smoke",
    query_id: "manual-query",
    relevant_ids: "",
    retrieved_ids: "",
    k_values: "1,3,5",
  });
  const [classification, setClassification] = useState({
    suite: "classification-smoke",
    labels: "",
    cases_json: '[{"id":"1","expected":"upi","predicted":"upi"}]',
  });

  const query = useAsync(
    () => http.get<QualityOverview>(`/observability/quality${qs({ limit: 30 })}`),
    [version]
  );

  const overview = query.data ?? null;
  const recent = overview?.recent_evals ?? [];
  const shadow = overview?.latest_shadow ?? null;
  const shadowMetrics = shadow?.metrics ?? {};
  const shadowCases = Number(shadowMetrics.case_count) || 0;

  const reload = () => setVersion((v) => v + 1);

  const splitList = (value: string) =>
    value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);

  const runLockedGate = async () => {
    setBusy("gate");
    try {
      // READ THE VERDICT, DO NOT INFER IT FROM THE HTTP CODE. The gate answers 200
      // whatever it decided, so `passed`, `failed` and `no_data` all arrive here as a
      // successful request. This button used to toast "passed" on any 200, which was
      // harmless only while the gate could not fail. It can now, and a button that
      // reports a pass next to a tile reading `no_data` is the same defect the gate was
      // fixed to remove: a green light wired to nothing.
      const res = await http.post<Record<string, unknown>>("/observability/quality/locked-gate", {
        suite: "core-runtime-locked",
        minimum_pass_rate: 1,
      });
      const status = String(res?.status ?? "unknown");
      if (status === "passed") {
        toast.push("Locked eval gate passed", "good");
      } else if (status === "no_data") {
        // Not a pass and not a failure. The gate refused to certify because the evidence
        // it needs is missing, which is the honest answer and must not read as either.
        toast.push(
          `Locked eval gate could not certify: ${String(res?.reason ?? "no evidence")}`,
          "warning",
        );
      } else {
        toast.push(
          `Locked eval gate ${status}: ${String(res?.reason ?? "see the gate row for detail")}`,
          "critical",
        );
      }
    } catch (err) {
      toast.push(errText(err, "Locked eval gate failed"), "critical");
    } finally {
      setBusy(null);
      // Refetched either way: a failed gate still writes a result row, and the
      // gate-status tile has to show it.
      reload();
    }
  };

  const runShadow = async () => {
    setBusy("shadow");
    const side = {
      classification: "upi",
      graph_hit: true,
      guardrail_ok: true,
      latency_ms: 0,
      total_tokens: 0,
    };
    try {
      await http.post<Record<string, unknown>>("/observability/shadow/run", {
        suite: "manual-shadow",
        cases: [
          {
            id: "dashboard-smoke",
            current: side,
            candidate: side,
            metadata: { created_from: "quality_dashboard" },
          },
        ],
        metadata: { created_from: "quality_dashboard" },
      });
      toast.push("Shadow comparison recorded", "good");
      reload();
    } catch (err) {
      toast.push(errText(err, "Could not run the shadow comparison"), "critical");
    } finally {
      setBusy(null);
    }
  };

  const runMakerChecker = async () => {
    setBusy("maker");
    try {
      const total = Math.max(25, Math.min(Number(mcTotal) || 200, 500));
      const result = await http.post<MakerCheckerResult>(
        "/observability/quality/maker-checker",
        { total, batch_size: 25 }
      );
      setMaker(result);
      toast.push(`Maker/Checker run complete (${result.total} cases)`, "good");
    } catch (err) {
      toast.push(errText(err, "Could not run maker/checker"), "critical");
    } finally {
      setBusy(null);
    }
  };

  const runRetrieval = async () => {
    setBusy("retrieval");
    try {
      const kValues = splitList(retrieval.k_values)
        .map(Number)
        .filter((v) => Number.isFinite(v) && v > 0);
      await http.post<Record<string, unknown>>("/observability/quality/retrieval-eval", {
        suite: retrieval.suite,
        query_id: retrieval.query_id,
        relevant_ids: splitList(retrieval.relevant_ids),
        retrieved_ids: splitList(retrieval.retrieved_ids),
        k_values: kValues.length ? kValues : [1, 3, 5],
        metadata: { created_from: "quality_dashboard" },
      });
      toast.push("Retrieval eval recorded", "good");
      reload();
    } catch (err) {
      toast.push(errText(err, "Could not run the retrieval eval"), "critical");
    } finally {
      setBusy(null);
    }
  };

  const runClassification = async () => {
    setBusy("classification");
    try {
      const parsed: unknown = JSON.parse(classification.cases_json);
      if (!Array.isArray(parsed)) throw new Error("Cases must be an array");
      await http.post<Record<string, unknown>>("/observability/quality/classification-eval", {
        suite: classification.suite,
        labels: splitList(classification.labels),
        cases: parsed,
        metadata: { created_from: "quality_dashboard" },
      });
      toast.push("Classification eval recorded", "good");
      reload();
    } catch (err) {
      toast.push(errText(err, "Could not run the classification eval"), "critical");
    } finally {
      setBusy(null);
    }
  };

  const evalColumns: Column<QualityEval>[] = [
    {
      key: "recorded_at",
      header: "Time",
      sortable: true,
      render: (r) => <span className="nowrap muted">{stamp(r.recorded_at)}</span>,
    },
    { key: "suite", header: "Suite", sortable: true },
    {
      key: "task_type",
      header: "Task",
      sortable: true,
      render: (r) => <Badge tone="info">{r.task_type}</Badge>,
    },
    {
      key: "metrics",
      header: "Metrics",
      render: (r) => (
        <span className="muted">
          {Object.entries(r.metrics ?? {})
            .map(([key, value]) => `${key}: ${Number(value).toFixed(3)}`)
            .join("  ")}
        </span>
      ),
    },
    {
      key: "run",
      header: "",
      align: "right",
      render: (r) => {
        const runId = r.metadata?.run_id;
        if (typeof runId !== "string" || !runId) return null;
        // New tab on purpose. The shell's router keys off `location.pathname`,
        // so an in-app navigation carrying `?run=` would not match a route; a
        // fresh load matches the path and still hands Model Lab the query.
        return (
          <a
            className="btn sm ghost"
            target="_blank"
            rel="noreferrer"
            href={`/chatbot/model-lab?run=${encodeURIComponent(runId)}`}
          >
            Open run
          </a>
        );
      },
    },
  ];

  const categoryRows = Object.entries(maker?.by_category ?? {});

  const accuracyTone = (accuracy: number): BadgeTone =>
    accuracy >= 0.8 ? "good" : accuracy >= 0.5 ? "warning" : "critical";

  /**
   * The server sends 0.0 for a rate whose denominator was empty, and the three
   * denominators are different: accuracy needs a case, recall needs a scam in
   * the batch, precision needs something to have been flagged. A batch with no
   * scam in it would otherwise report 0% recall, which claims the model missed
   * every scam when there was none to miss.
   */
  const MakerRate = ({
    label,
    value,
    denominator,
    why,
    digits = 0,
  }: {
    label: string;
    value: number;
    denominator: number;
    why: string;
    digits?: number;
  }) =>
    denominator > 0 ? (
      <StatTile
        label={label}
        value={digits > 0 ? (Number(value) || 0).toFixed(digits) : roundPct(value)}
      />
    ) : (
      <NotMeasuredTile label={label} why={why} />
    );

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Quality</h1>
          <div className="sub">
            Retrieval, classification, guardrail and active-learning quality signals.
          </div>
        </div>
        <div className="page-actions">
          <button className="btn" disabled={query.loading} onClick={reload}>
            {query.loading ? <Spinner label="Refreshing" /> : "⟲"} Refresh
          </button>
          {canRun && (
            <>
              <button className="btn" disabled={busy !== null} onClick={() => void runLockedGate()}>
                {busy === "gate" ? <Spinner label="Running" /> : "▷"} Locked gate
              </button>
              <button className="btn" disabled={busy !== null} onClick={() => void runShadow()}>
                {busy === "shadow" ? <Spinner label="Running" /> : "▷"} Shadow
              </button>
            </>
          )}
          <a className="btn" href="/chatbot/model-lab">
            Model Lab
          </a>
        </div>
      </div>

      {query.error && (
        <Banner tone="critical" traceId={query.error.traceId}>
          Could not load quality data: {query.error.message}
        </Banner>
      )}

      <EvalHistoryPanel />

      <div className="grid c4" style={{ marginBottom: "var(--sp-4)" }}>
        {QUALITY_METRICS.map(([label, key]) => (
          <div className="stat" key={key}>
            <div className="label">{label}</div>
            <div className="value">{query.loading ? <Spinner /> : num(overview ?? undefined, key)}</div>
          </div>
        ))}
        <div className="stat">
          <div className="label">Gate status</div>
          <div className="value">
            {query.loading ? <Spinner /> : String(overview?.latest_locked_gate_status ?? "none")}
          </div>
        </div>
        <div className="stat">
          <div className="label">Drift status</div>
          <div className="value">
            {query.loading ? <Spinner /> : overview?.latest_drift?.status ?? "not_run"}
          </div>
        </div>
        <div className="stat">
          <div className="label">Shadow status</div>
          <div className="value">{query.loading ? <Spinner /> : shadow?.status ?? "not_run"}</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: "var(--sp-4)" }}>
        <div className="row">
          <div>
            <h3>Maker / Checker batch test</h3>
            <div className="muted">
              Sends fresh synthetic scam and benign messages through the live KG-grounded pipeline in
              batches of 25, and scores detection plus the reason. The cases are generated at run
              time and are NOT drawn from the ingested graph.
            </div>
          </div>
          <span className="spacer" />
          <input
            type="number"
            min={25}
            max={500}
            value={mcTotal}
            aria-label="Cases to generate"
            style={{ width: 100 }}
            onChange={(e) => setMcTotal(Number(e.target.value) || 200)}
          />
          <button
            className="btn primary"
            disabled={!canRun || busy !== null}
            onClick={() => void runMakerChecker()}
          >
            {busy === "maker" ? <Spinner label="Running" /> : "▷"} Run batch
          </button>
        </div>

        {busy === "maker" ? (
          <div className="col" style={{ marginTop: "var(--sp-4)", alignItems: "center" }}>
            <Spinner label="Running" />
            <span className="muted">Running fresh cases through the live model…</span>
          </div>
        ) : !maker ? (
          <p className="muted" style={{ marginTop: "var(--sp-4)" }}>
            Run a batch to test the model on fresh cases and see accuracy plus why each miss happened.
          </p>
        ) : (
          <>
            <div className="grid c4" style={{ marginTop: "var(--sp-4)" }}>
              <MakerRate
                label="Accuracy"
                value={maker.overall.accuracy}
                denominator={maker.overall.n}
                why="the batch graded no case"
              />
              <MakerRate
                label="Precision"
                value={maker.overall.precision}
                denominator={maker.overall.tp + maker.overall.fp}
                why="nothing was flagged as a scam"
              />
              <MakerRate
                label="Recall"
                value={maker.overall.recall}
                denominator={maker.overall.tp + maker.overall.fn}
                why="the batch contained no scam to detect"
              />
              <MakerRate
                label="F1"
                value={maker.overall.f1}
                denominator={Math.min(
                  maker.overall.tp + maker.overall.fp,
                  maker.overall.tp + maker.overall.fn
                )}
                why="precision or recall has no denominator"
                digits={2}
              />
              <StatTile
                label="Detected / scams"
                value={`${maker.overall.tp}/${maker.overall.tp + maker.overall.fn}`}
              />
              <StatTile label="False alarms" value={maker.overall.fp} />
            </div>
            <div className="muted" style={{ marginTop: "var(--sp-3)" }}>
              {maker.total} cases · decided by KG {maker.decided_by.kg} / rule {maker.decided_by.rule} ·{" "}
              {maker.note}
            </div>

            <div className="grid c2" style={{ marginTop: "var(--sp-4)" }}>
              <div className="card flush">
                <div className="card-head">
                  <h3>Accuracy by category</h3>
                </div>
                <div className="card-body">
                  {categoryRows.length === 0 ? (
                    <div className="muted">No categories in this run.</div>
                  ) : (
                    <div className="col">
                      {categoryRows.map(([cat, m]) => (
                        <div className="row" key={cat}>
                          <span className="truncate">{cat}</span>
                          <span className="muted num">n={m.n}</span>
                          <span className="spacer" />
                          {/* No case in the category means no accuracy for it. */}
                          {m.n > 0 ? (
                            <Badge tone={accuracyTone(m.accuracy)}>{roundPct(m.accuracy)}</Badge>
                          ) : (
                            <span className="muted">not measured</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="card flush">
                <div className="card-head">
                  <h3>Misclassifications ({maker.misclassifications.length})</h3>
                  <span className="muted">with reason</span>
                </div>
                <div className="card-body" style={{ maxHeight: 320, overflow: "auto" }}>
                  {maker.misclassifications.length === 0 ? (
                    <div className="muted">No misclassifications.</div>
                  ) : (
                    <div className="col">
                      {maker.misclassifications.map((m) => (
                        <div key={m.id}>
                          <div className="row">
                            <Badge tone={m.is_scam ? "critical" : "warning"}>
                              {m.is_scam ? "MISSED" : "FALSE-ALARM"}
                            </Badge>
                            <span className="muted">
                              {m.category}/{m.language} → {m.pred}
                            </span>
                          </div>
                          <div className="prewrap">{m.text}</div>
                          <div className="muted prewrap">{m.reason}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="grid c2" style={{ marginBottom: "var(--sp-4)" }}>
        <div className="card">
          <div className="row">
            <h3>Shadow comparison</h3>
            <span className="spacer" />
            <Badge tone="neutral">{shadow?.status ?? "not_run"}</Badge>
          </div>
          {/* No shadow run means no comparison. A disagreement rate of 0.000
              beside a status of not_run reads as "the candidate agrees with
              production everywhere", which is the strongest claim on this page
              and nobody made it. */}
          {shadowCases > 0 ? (
            <div className="grid c2" style={{ marginTop: "var(--sp-3)" }}>
              <StatTile
                label="Disagreement"
                value={(Number(shadowMetrics.classification_disagreement_rate) || 0).toFixed(3)}
              />
              <StatTile label="Cases" value={shadowCases} />
              <StatTile
                label="Retrieval changes"
                value={Number(shadowMetrics.retrieval_hit_changed_count) || 0}
              />
              <StatTile
                label="Guardrail changes"
                value={Number(shadowMetrics.guardrail_changed_count) || 0}
              />
            </div>
          ) : (
            <div className="grid c2" style={{ marginTop: "var(--sp-3)" }}>
              <NotMeasuredTile
                label="Disagreement"
                why="no shadow case has been compared yet"
              />
            </div>
          )}
        </div>

        <div className="card">
          <div className="row">
            <h3>Retrieval eval</h3>
            <span className="spacer" />
            <Badge tone="neutral">MRR / recall / nDCG</Badge>
          </div>
          <div className="col" style={{ marginTop: "var(--sp-3)" }}>
            <Field label="Suite">
              <input
                value={retrieval.suite}
                onChange={(e) => setRetrieval({ ...retrieval, suite: e.target.value })}
              />
            </Field>
            <Field label="Query id">
              <input
                value={retrieval.query_id}
                onChange={(e) => setRetrieval({ ...retrieval, query_id: e.target.value })}
              />
            </Field>
            <Field label="Relevant ids" hint="Comma separated">
              <input
                value={retrieval.relevant_ids}
                onChange={(e) => setRetrieval({ ...retrieval, relevant_ids: e.target.value })}
              />
            </Field>
            <Field label="Retrieved ids" hint="Comma separated, in rank order">
              <input
                value={retrieval.retrieved_ids}
                onChange={(e) => setRetrieval({ ...retrieval, retrieved_ids: e.target.value })}
              />
            </Field>
            <Field label="K values" hint="Comma separated; falls back to 1,3,5">
              <input
                value={retrieval.k_values}
                onChange={(e) => setRetrieval({ ...retrieval, k_values: e.target.value })}
              />
            </Field>
            <button
              className="btn primary"
              disabled={!canRun || busy !== null}
              onClick={() => void runRetrieval()}
            >
              {busy === "retrieval" ? <Spinner label="Running" /> : "▷"} Run
            </button>
          </div>
        </div>

        <div className="card">
          <div className="row">
            <h3>Classification eval</h3>
            <span className="spacer" />
            <Badge tone="neutral">Accuracy / macro F1</Badge>
          </div>
          <div className="col" style={{ marginTop: "var(--sp-3)" }}>
            <Field label="Suite">
              <input
                value={classification.suite}
                onChange={(e) => setClassification({ ...classification, suite: e.target.value })}
              />
            </Field>
            <Field label="Labels" hint="Comma separated">
              <input
                value={classification.labels}
                onChange={(e) => setClassification({ ...classification, labels: e.target.value })}
              />
            </Field>
            <Field label="Cases JSON" hint="An array of {id, expected, predicted}">
              <textarea
                rows={6}
                value={classification.cases_json}
                onChange={(e) =>
                  setClassification({ ...classification, cases_json: e.target.value })
                }
              />
            </Field>
            <button
              className="btn primary"
              disabled={!canRun || busy !== null}
              onClick={() => void runClassification()}
            >
              {busy === "classification" ? <Spinner label="Running" /> : "▷"} Run
            </button>
          </div>
        </div>
      </div>

      <div className="card flush">
        <div className="card-head">
          <h3>Recent evals</h3>
        </div>
        <Table
          columns={evalColumns}
          rows={recent}
          loading={query.loading}
          empty="No quality evals recorded yet"
        />
      </div>
    </div>
  );
}

